import { Controller } from "@hotwired/stimulus"

// Drives the post-meeting minutes states. Polls the meeting's sync_status
// endpoint and reloads the page when the state changes so the server can
// render the right thing.
//
// Two modes (set via data-meeting-sync-mode-value):
//   "generating" — the "Generating Minutes…" spinner. Reloads when the pull
//      lands (synced) OR when it stalls, so the server swaps the spinner for
//      the manual-draft fallback.
//   "stalled"    — the fallback message is already showing. Keeps polling
//      quietly for a *late* transcript and reloads ONLY when it actually
//      lands. Never reloads on `stalled` again (that would loop forever).
//
// Both modes stop after maxAttempts so an abandoned meeting never polls
// indefinitely. Stops polling on disconnect.
export default class extends Controller {
  static values = {
    statusUrl: String,
    interval: { type: Number, default: 15000 },
    mode: { type: String, default: "generating" },
    maxAttempts: { type: Number, default: 120 }
  }

  connect() {
    this._poll = this._poll.bind(this)
    this._attempts = 0
    this._timer = setTimeout(this._poll, this.intervalValue)
  }

  disconnect() {
    if (this._timer) clearTimeout(this._timer)
  }

  async _poll() {
    this._attempts++
    try {
      const resp = await fetch(this.statusUrlValue, {
        headers: { Accept: "application/json" }
      })
      if (resp.ok) {
        const data = await resp.json()
        // Transcript landed → reload to reveal the drafted minutes. Works in
        // both modes (covers a late transcript after the fallback showed).
        if (data.synced) { window.location.reload(); return }
        // First time we cross into "stalled", reload once so the server
        // renders the fallback instead of the spinner. Only the generating
        // poller does this — the stalled poller must NOT, or it'd reload
        // endlessly on every poll.
        if (this.modeValue === "generating" && (data.stalled || data.generating === false)) {
          window.location.reload()
          return
        }
      }
    } catch (e) {
      /* transient network error — keep polling */
    }
    if (this._attempts >= this.maxAttemptsValue) return // give up quietly
    this._timer = setTimeout(this._poll, this.intervalValue)
  }
}
